import { Checklist, Download, PriceCheck } from '@mui/icons-material'
import { Autocomplete, Button, Divider, FormControlLabel, ListItemIcon, ListItemText, MenuItem, Paper, Radio, RadioGroup, TextField } from '@mui/material'
import { Filters, TableFormat } from './Billings'
import { utils, writeFile } from 'xlsx'
import { AutoCompleteOptions } from '../../../../hooks/useLookupTables'
import { useCallback, useMemo } from 'react'
import { NewViewBilling } from '../types'

type Props = {
  data: NewViewBilling[]
  filters: Filters
  handleMarkAsPaid: () => Promise<void>
  selectedInstallments: number[]
  setEnableMultipleSelect: React.Dispatch<React.SetStateAction<boolean>>
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  tableFormat: TableFormat[]
}

export default function BillingsOptions({
  data,
  filters,
  handleMarkAsPaid,
  selectedInstallments,
  setEnableMultipleSelect,
  setFilters,
  tableFormat,
}: Props) {
  // Options for filtering
  const currenciesOptions = useMemo(() => {
    const set: Set<string> = new Set()

    for (const _ of data) {
      set.add(JSON.stringify({ id: _.fk_id_currency, label: _.currency_name }))
    }

    return Array.from(set).map((_) => JSON.parse(_) as AutoCompleteOptions).sort((a, b) => a.id - b.id)
  }, [data])

  const defaultBillingsOptions = useMemo(() => {
    const set: Set<string> = new Set()

    for (const _ of data) {
      if (_.fk_id_default_billing) set.add(JSON.stringify({ id: _.fk_id_default_billing, label: _.default_billing_name }))
    }

    return Array.from(set).map((_) => JSON.parse(_) as AutoCompleteOptions).sort((a, b) => a.id - b.id)
  }, [data])

  const gradesOptions = useMemo(() => {
    const set: Set<string> = new Set()

    for (const _ of data) {
      set.add(JSON.stringify({ id: _.beneficiary.fk_id_grade, label: _.beneficiary.grade_name }))
    }

    return Array.from(set).map((_) => JSON.parse(_) as AutoCompleteOptions).sort((a, b) => a.id - b.id)
  }, [data])

  const statusesOptions = useMemo(() => {
    const set: Set<string> = new Set()

    for (const _ of data) {
      set.add(JSON.stringify({ id: _.beneficiary.fk_id_student_status_type, label: _.beneficiary.student_status_type_name }))
    }

    return Array.from(set).map((_) => JSON.parse(_) as AutoCompleteOptions).sort((a, b) => a.id - b.id)
  }, [data])

  const exportToExcel = useCallback(() => {
    const wb = utils.book_new()

    const sheets = [
      { value: 'due_value', name: 'Due Values' },
      { value: 'gross_value', name: 'Gross Values' },
      { value: 'paid_value', name: 'Paid Values' },
    ] as const

    for (const _sheet of sheets) {
      const page: any = []

      for (const customer of tableFormat) {
        for (const billing of customer.billings) {
          page.push({
            'ID': billing.id,
            'Beneficiary/Payer': customer.customerName,
            'Grade': customer.gradeName,
            'Bundle/Def. Bill.': customer.bundleDefaultBilling,
            'Status': customer.studentStatusTypeName,
            'Line Total': billing.totalDueValue,
            'Number of Payments': billing.numberOfInstallments,
            ...billing.cashFlow.reduce((prev: Record<string, number>, curr) => {
              prev[curr.month] = curr.dueValue

              return prev
            }, {})
          })
        }
      }

      const ws = utils.json_to_sheet(page)
      utils.book_append_sheet(wb, ws, _sheet.name)
    }

    writeFile(wb, `Billings - ${filters.affiliation?.label} - from ${filters.startDate?.toISODate()} to ${filters.endDate?.toISODate()}.xlsx`)
  }, [tableFormat])

  return <>
    <MenuItem
      onClick={() => setEnableMultipleSelect((prev) => !prev)}
    >
      <ListItemIcon>
        <Checklist />
      </ListItemIcon>
      <ListItemText>
        Select Multiple Rows
      </ListItemText>
    </MenuItem>

    <MenuItem
      onClick={handleMarkAsPaid}
      disabled={!selectedInstallments.length}
    >
      <ListItemIcon>
        <PriceCheck />
      </ListItemIcon>
      <ListItemText>
        Mark installments as paid
      </ListItemText>
    </MenuItem>

    <Divider />

    <MenuItem>
      <Paper>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
          value={filters.value}
          onChange={(e) => setFilters((_filters) => ({ ..._filters, value: e.target.value as 'gross' | 'due' | 'paid' }))}
          row
        >
          <FormControlLabel value="gross" control={<Radio sx={{ padding: .25 }} />} label="Gross" />
          <FormControlLabel value="due" control={<Radio sx={{ padding: .25 }} />} label="Due" />
          <FormControlLabel value="paid" control={<Radio sx={{ padding: .25 }} />} label="Paid" />
        </RadioGroup>
      </Paper>
    </MenuItem>

    <MenuItem>
      <Autocomplete
        id='filter-grades'
        options={gradesOptions}
        value={filters.grades}
        onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, grades: newValue }))}
        renderInput={(params) => <TextField {...params} label="Grades" />}
        multiple
        disableCloseOnSelect
        fullWidth
      />
    </MenuItem>

    <MenuItem>
      <Autocomplete
        id='filter-statuses'
        options={statusesOptions}
        value={filters.statuses}
        onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, statuses: newValue }))}
        renderInput={(params) => <TextField {...params} label="Statuses" />}
        multiple
        disableCloseOnSelect
        fullWidth
      />
    </MenuItem>

    <MenuItem>
      <Autocomplete
        id='filter-default-billings'
        options={defaultBillingsOptions}
        value={filters.defaultBillings}
        onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, defaultBillings: newValue }))}
        renderInput={(params) => <TextField {...params} label="Default Billings" />}
        multiple
        disableCloseOnSelect
        fullWidth
      />
    </MenuItem>

    <MenuItem>
      <Autocomplete
        id='filter-currencies'
        options={currenciesOptions}
        value={filters.currency}
        onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, currency: newValue }))}
        renderInput={(params) => <TextField {...params} label="Currency" />}
        fullWidth
      />
    </MenuItem>

    <MenuItem>
      <Button
        id='button-export-excel'
        startIcon={<Download />}
        color='success'
        sx={{ color: 'white', backgroundColor: 'hsl(148.2, 56.5%, 35%)' }}
        disabled={!data.length}
        onClick={exportToExcel}
      >
        XLSX
      </Button>
    </MenuItem>
  </>
}
